import { React, useState, useEffect, useCallback } from 'react';
import {
    Box,
    Chip,
    Skeleton,
    Slider,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { AutoRefresh } from '../../utils/auto-refresh';
import { AdminMenuBar } from '../../utils/admin-menu-bar';
import {
    AdminCacheStats as AdminCacheStatsApi,
    AdminCacheStatsControl as AdminCacheStatsControlApi,
} from '../../../api';

// Hit rate window: tuning is live, so only the recent past is of interest.
const HOURS = 24;
// Hit rate shown green from here, orange below (display only, the TTL does not depend on it).
const GOOD_RATE = 0.8;

const CELL = { whiteSpace: 'nowrap', py: 0.5, px: 1 };
const HEAD_CELL = { ...CELL, fontWeight: 600 };

// 1234 -> 1.2K, 3400000 -> 3.4M
const compact = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });

const duration = (ms) => {
    if (ms == null) return '—';
    if (ms < 60000) return `${+(ms / 1000).toFixed(1)} s`;
    if (ms < 3600000) return `${+(ms / 60000).toFixed(1)} min`;
    return `${+(ms / 3600000).toFixed(1)} h`;
};

// Read-only slider from max (left) to min (right), the order the hit rate drives it (0% hits -> max, 100% -> min), with
// the TTL in use as the thumb. The slider itself runs 0..1 so the scale can be reversed; the labels carry the real
// values. With no flush yet the thumb sits at max (where a fresh isolate starts) and its label shows a dash.
function TtlSlider({ ttl, min, max }) {
    if (min == null || max == null) return '—';
    if (max <= min) return `${duration(min)} (fixed)`;
    const pos = (max - Math.min(max, Math.max(min, ttl ?? max))) / (max - min);
    return (
        <Box sx={{ width: 320, px: 4, pt: 3.5, pb: 0.5 }}>
            <Slider
                size='small'
                value={pos}
                min={0}
                max={1}
                step={0.001}
                valueLabelDisplay='on'
                valueLabelFormat={() => (ttl == null ? '—' : duration(ttl))}
                marks={[
                    { value: 0, label: duration(max) },
                    { value: 1, label: duration(min) },
                ]}
                sx={{ pointerEvents: 'none' }}
            />
        </Box>
    );
}

// One row per cache (config key): hit rate and lookups over the last 24 hours, the TTL in use at the latest
// flush on a max - min slider. The loise server sets the TTL from the hit rate: 0% -> max, 100% -> min.
// The loise servers only record these while a measurement runs (Command > Start / Stop), so the rows are what that
// measurement collected. A start or stop reaches every server with its next config poll (about a minute).
function CacheStats() {
    const [state, setState] = useState({ loading: false, rows: [], enabled: false });
    const { rows, loading, enabled } = state;

    const refresh = useCallback(() => AdminCacheStatsApi(setState, HOURS), []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const control = (action) => AdminCacheStatsControlApi(action, refresh);
    const apiItems = [
        { label: 'Start', onClick: () => control('start'), disabled: enabled },
        { label: 'Stop', onClick: () => control('stop'), disabled: !enabled },
    ];

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <AdminMenuBar apiItems={apiItems} />
            <Box sx={{ m: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2,
                        flexShrink: 0,
                    }}>
                    <Chip
                        size='small'
                        label={enabled ? 'Measuring' : 'Stopped'}
                        color={enabled ? 'success' : 'default'}
                        variant={enabled ? 'filled' : 'outlined'}
                    />
                    <AutoRefresh onRefresh={refresh} defaultMs={60_000} />
                </Box>
                <Box sx={{ overflow: 'auto', flex: 1 }}>
                    {!loading && rows.length === 0 && (
                        <Typography variant='body2' color='text.secondary'>
                            No caches configured.
                        </Typography>
                    )}
                    {!loading && !enabled && rows.length > 0 && rows.every((r) => !r.total) && (
                        <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                            Not measuring. Command &gt; Start collects hit rates (it clears the old numbers first).
                        </Typography>
                    )}
                    {(loading || rows.length > 0) && (
                        <Table size='small' sx={{ width: '100%', '& td, & th': { border: 1, borderColor: 'divider' } }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={HEAD_CELL}>Key</TableCell>
                                    <TableCell sx={HEAD_CELL}>Hit rate</TableCell>
                                    <TableCell sx={HEAD_CELL}>Lookups</TableCell>
                                    <TableCell sx={HEAD_CELL}>TTL (max - min)</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading && rows.length === 0
                                    ? Array.from({ length: 4 }, (_, i) => (
                                          <TableRow key={i}>
                                              {[0, 1, 2, 3].map((j) => (
                                                  <TableCell key={j} sx={CELL}>
                                                      <Skeleton />
                                                  </TableCell>
                                              ))}
                                          </TableRow>
                                      ))
                                    : rows.map((r) => (
                                          <TableRow key={r.name} hover>
                                              <TableCell sx={CELL}>{r.name}</TableCell>
                                              <TableCell sx={CELL}>
                                                  {r.total ? (
                                                      <Chip
                                                          label={`${((r.hits / r.total) * 100).toFixed(1)}%`}
                                                          size='small'
                                                          color={r.hits / r.total >= GOOD_RATE ? 'success' : 'warning'}
                                                      />
                                                  ) : (
                                                      '—'
                                                  )}
                                              </TableCell>
                                              <TableCell sx={CELL}>
                                                  <Tooltip title={r.total.toLocaleString()}>
                                                      <span>{compact.format(r.total)}</span>
                                                  </Tooltip>
                                              </TableCell>
                                              <TableCell sx={CELL}>
                                                  <TtlSlider ttl={r.ttl_ms} min={r.min_ms} max={r.max_ms} />
                                              </TableCell>
                                          </TableRow>
                                      ))}
                            </TableBody>
                        </Table>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

export { CacheStats };
